import React, { useEffect, useState } from 'react';
import PaymentMethodForm from './PaymentMethodForm';
import {
  Button,
  Card,
  Col,
  Flex,
  Image,
  message,
  Switch,
  Table,
  Typography,
} from 'antd';

import PageTitle from '../../components/PageTitle';
import IntlMessage from '../../components/IntlMessage';
import ActionBar from '../../components/ActionBar';
import { debounce } from 'lodash';
import FilterBar from '../../components/FilterBar';
import paymentMethodApi from '../../api/paymentMethod';
import useTime from '../../hooks/useTime';

const { Text } = Typography;

const PaymentMethod = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentMethodsData, setPaymentMethodsData] = useState([]);
  const [currentData, setCurrentData] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [total, setTotal] = useState(1);
  const [refresh, setRefresh] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    order_by: 'id',
    order_dir: 'DESC',
    keyword: '',
  });

  const { convertToAnyTimezone } = useTime();

  const showModal = () => {
    setIsModalOpen(true);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onModalOpen = (record) => {
    setCurrentData(record);
    setIsModalOpen(true);
  };

  const onModalClose = (record) => {
    setCurrentData('');
    setIsModalOpen(false);
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const payment = await paymentMethodApi.getAll(filter);
        setPaymentMethodsData(payment.data);
        setTotal(payment.total);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [refresh, filter]);

  const columns = [
    {
      title: <IntlMessage id="global.title" />,
      key: 'title',
      dataIndex: 'title',
    },
    {
      title: <IntlMessage id="global.gateway_url" />,
      key: 'gateway_url',
      sorter: true,
      dataIndex: 'gateway_url',
      render: (text, record) => {
        return (
          <Button
            type="link"
            size="small"
            onClick={() => onModalOpen(record)}
            shape="round">
            {record.gateway_url}
          </Button>
        );
      },
    },
    {
      title: <IntlMessage id="global.image" />,
      key: 'image',
      dataIndex: 'image',
      render: (_, record) => {
        return record.image ? <Image width={50} src={record.image} /> : '';
      },
    },
    {
      title: <IntlMessage id="global.method" />,
      key: 'method',
      sorter: true,
      dataIndex: 'method',
    },
    {
      title: <IntlMessage id="global.display" />,
      key: 'display',
      sorter: true,
      dataIndex: 'display',
      render: (text, record, index) => {
        return record.display;
      },
    },
    {
      title: <IntlMessage id="global.status" />,
      key: 'status',
      dataIndex: 'status',
      render: (text, record, index) => {
        return (
          <Flex justify="center">
            <Switch
              defaultValue={record.status}
              checked={!!text}
              onChange={() => onChangeStatus(record, !text)}
            />
          </Flex>
        );
      },
    },
    {
      title: <IntlMessage id="global.created_at" />,
      key: 'created_at',
      dataIndex: 'created_at',
      sorter: true,
      render: (text, record, index) => {
        return (
          <Text>
            {convertToAnyTimezone(record?.created_at).format(
              'DD/MM/YYYY HH:mm',
            )}
          </Text>
        );
      },
    },
  ];

  const getOrder = (order) => {
    if (order === 'ascend') return 'ASC';
    if (order === 'descend') return 'DESC';
    return 'DESC';
  };

  const onChangeTable = (pagination, filters, sorter) => {
    if (pagination) {
      setFilter({
        ...filter,
        page: pagination.current,
        limit: pagination.pageSize,
      });
    }

    if (sorter) {
      const { field, order } = sorter;
      setFilter({
        page: pagination.current,
        limit: pagination.pageSize,
        order_by: field ? field : 'id',
        order_dir: getOrder(order),
      });
    }

    if (pagination.pageSize !== filter.page) {
      setPaymentMethodsData([]);
    }
  };

  const onDelete = async () => {
    await paymentMethodApi.delete(selectedRowKeys).catch((err) => {
      console.log(err);
    });
    message.success('delete success');
    onRefresh();
  };

  const onRefresh = () => {
    setRefresh((refresh) => !refresh);
  };

  const onCopy = async () => {
    if (selectedRowKeys.length > 0) {
      for (const key of selectedRowKeys) {
        await paymentMethodApi.copy(key).catch((err) => {
          console.log(err);
        });
      }
      message.success('copy success');
      onRefresh();
    }
  };

  const onFilter = debounce((value, name, type) => {
    if (type === 'search') {
      setFilter((filter) => {
        return { ...filter, keyword: value, page: 1 };
      });
    } else {
    }
  }, 300);

  const onChangeStatus = async (record, checked) => {
    await paymentMethodApi
      .updateStatus(record.id, { status: checked })
      .catch((err) => {
        console.log(err);
      });
    setPaymentMethodsData((data) => {
      return data.map((item) => {
        if (item.id === record.id) {
          return {
            ...item,
            status: checked,
          };
        }
        return item;
      });
    });
  };

  return (
    <React.Fragment>
      <Flex justify="space-between" align="center">
        <Col>
          <PageTitle title={<IntlMessage id="global.payment-method" />} />
        </Col>
        <Col>
          <ActionBar
            isShowAddButton={true}
            isShowCopyButton={true}
            onAdd={showModal}
            isShowDeleteButton={true}
            onCopy={onCopy}
            deleteMessage={
              'Vous êtes sur le point de supprimer ce moyen de paiement et les enregistrements qui lui sont associés. Voulez-vous vraiment supprimer ce moyen de paiement ?'
            }
            isShowPublishButtons={false}
            onDelete={onDelete}
            isDisabled={selectedRowKeys.length > 0 ? false : true}
            onRefresh={onRefresh}
          />
        </Col>
      </Flex>
      <Card bordered={false}>
        <Flex vertical gap={8}>
          <FilterBar onFilter={onFilter} />

          <Table
            columns={columns}
            pagination={{
              limit: filter.limit,
              current: filter.page,
              total: total,
              showSizeChanger: true,
            }}
            size="small"
            onChange={onChangeTable}
            dataSource={paymentMethodsData}
            rowSelection={rowSelection}
            loading={loading}
            rowKey={'id'}
          />
        </Flex>
      </Card>
      {isModalOpen ? (
        <PaymentMethodForm
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onModalClose={onModalClose}
          currentData={currentData}
          onRefresh={onRefresh}
        />
      ) : null}
    </React.Fragment>
  );
};

export default PaymentMethod;
